import React, {useMemo, useState} from 'react';
import {View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {EmptyState, PrimaryButton, SectionTitle} from '../components/management/ManagementUI';
import {ModuleTileGrid, SearchField} from '../components/management/ManagementHubKit';
import ManagementScreen from '../components/management/ManagementScreen';
import DemoModeToggleButton from '../components/DemoModeToggleButton';
import {GreetingHeader} from '../components/premium/PremiumKit';
import {useManagementAccess} from '../helpers/managementAccess';
import {useManagementData} from '../helpers/ManagementDataContext';
import {useManagementTheme} from '../helpers/managementTheme';
import {useCurrentUser} from '../helpers/useCurrentUser';
import {MANAGEMENT_MODULES, groupModulesBySection} from '../config/managementModules';
import useAiAssistantGate from './assistant/components/useAiAssistantGate';
import {useChatHubBadge} from '../helpers/teamChat/useChatHubBadge';

/**
 * Modules hub — the full, sectioned module catalog of the Manager app, mirroring
 * the admin sidebar taxonomy (Sales / Main modules / Functional / General &
 * system). Tiles come from the shared {@link module:config/managementModules}
 * catalog; each is gated by `useManagementAccess().canAccess(key)` (license +
 * role + per-user visibility) and navigates to a real native screen or a
 * ManagementListScreen type. A search field filters tiles by their localized
 * title/subtitle so users find modules without scanning the whole catalog.
 *
 * Premium layout: greeting header (avatar → settings, store chip), a pill search
 * field and two-up tinted-chip tiles per section; locked modules keep their muted
 * tile and reason.
 *
 * @returns {JSX.Element} The modules hub.
 */
export default function ManagementDataScreen() {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const {canAccess} = useManagementAccess();
    const {scope} = useManagementData();
    const {user: currentUser} = useCurrentUser();
    // "Ask anything" AI assistant — build-flag + store-level gate (function.aiAssistant).
    const aiAssistantEnabled = useAiAssistantGate();
    // Team Chat entry (unread count in the subtitle); hidden when chat is excluded.
    // Messenger hub entry: team chat + live support unread combined.
    const {visible: teamChatVisible, total: teamChatUnread} = useChatHubBadge();
    const [query, setQuery] = useState('');

    const open = (tile) => {
        if (!canAccess(tile.key).allowed) return;
        if (tile.screen) navigation.navigate(tile.screen, tile.params);
        else if (tile.list) navigation.navigate('ManagementListScreen', {type: tile.list});
    };

    // Filter by the *localized* title/subtitle so search works in every locale,
    // then group into the admin-mirroring sections (empty sections drop out).
    const groups = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const visible = needle
            ? MANAGEMENT_MODULES.filter((tile) => `${t(tile.titleKey)} ${t(tile.subtitleKey)}`.toLowerCase().includes(needle))
            : MANAGEMENT_MODULES;

        return groupModulesBySection(visible);
    }, [query, t]);

    // "Ask anything" AI assistant + Team chat — pinned above the module catalog
    // (mirrors the Mail entry pattern of the other mode menus), hidden when the
    // store/build disables the module or while a search is active.
    const pinnedTiles = [];

    if (aiAssistantEnabled && !query.trim()) {
        pinnedTiles.push({
            key: 'aiAssistant',
            icon: 'robot-outline',
            tone: 'primary',
            title: t('assistant_title', 'AI asszisztens'),
            subtitle: t('assistant_menu_subtitle', 'Kérdezz bármit — adatok, riportok és műveletek'),
            onPress: () => navigation.navigate('AIAssistantScreen'),
        });
    }

    if (teamChatVisible && !query.trim()) {
        pinnedTiles.push({
            key: 'teamChat',
            icon: 'forum-outline',
            tone: 'warning',
            title: t('team_chat_hub_title'),
            subtitle: teamChatUnread > 0
                ? t('team_chat_unread_count', {count: teamChatUnread})
                : t('team_chat_menu_subtitle'),
            onPress: () => navigation.navigate('ChatHomeScreen'),
        });
    }

    return (
        <ManagementScreen safeTop>
            <GreetingHeader
                user={currentUser}
                onAvatarPress={() => navigation.navigate('ManagementSettings')}
                eyebrow={t('mgmt_data_hero_eyebrow', {defaultValue: 'Management modules'})}
                title={t('mgmt_data_hero_title')}
                subtitle={t('mgmt_data_hero_subtitle')}
                chipLabel={scope?.restaurant?.name || undefined}
                right={<DemoModeToggleButton color={m.colors.subText} />}
            />

            <SearchField
                value={query}
                onChangeText={setQuery}
                placeholder={t('mgmt_data_search_placeholder')}
                clearLabel={t('mgmt_data_search_clear', {defaultValue: 'Keresés törlése'})}
            />

            {pinnedTiles.length > 0 && <ModuleTileGrid items={pinnedTiles} />}

            {groups.length === 0 && (
                <EmptyState
                    icon="magnify-close"
                    title={t('mgmt_data_search_empty_title')}
                    text={t('mgmt_data_search_empty_text')}
                    action={query.trim() ? (
                        <PrimaryButton
                            variant="outline"
                            icon="close"
                            label={t('mgmt_data_search_clear', {defaultValue: 'Keresés törlése'})}
                            onPress={() => setQuery('')}
                        />
                    ) : null}
                />
            )}

            {groups.map(({section, tiles}) => (
                <View key={section.id}>
                    <SectionTitle title={t(section.titleKey)} icon={section.icon} tone={section.tone} eyebrow />
                    <ModuleTileGrid
                        items={tiles.map((tile) => {
                            const access = canAccess(tile.key);

                            return {
                                key: tile.id || tile.key,
                                icon: tile.icon,
                                tone: tile.tone,
                                title: t(tile.titleKey),
                                subtitle: t(tile.subtitleKey),
                                disabled: !access.allowed,
                                lockedReason: access.reason,
                                onPress: () => open(tile),
                            };
                        })}
                    />
                </View>
            ))}
        </ManagementScreen>
    );
}
